/**
 * useTenantPipelinesAndStages — fetch a flat list of all stages across all
 * of the tenant's pipelines, each annotated with its parent pipeline's
 * name. Powers the Stage Mapping tab's combined "Pipeline / Stage"
 * searchable dropdown (single dropdown vs. cascading two-step) chosen so
 * admins mapping many fields don't have to switch pipelines per row.
 *
 * Used by:
 *   - AddStageMappingForm (Joules-stage selector)
 *   - StageMappingRow inline re-map dropdown
 *   - DefaultLandingStageSelector
 */

import { useQuery } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import { supabase } from '@/lib/supabaseClient';
import type { PipelineStageCandidate } from '@/types/integrations/stageMapping';

const FIVE_MINUTES = 5 * 60 * 1000;

interface RawStageRow {
  id: number;
  name: string;
  stage_order: number;
  pipeline_id: number;
}

interface RawPipelineRow {
  id: number;
  name: string;
  priority: number;
}

export function useTenantPipelinesAndStages() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;

  return useQuery({
    queryKey: ['stages', tenantId, 'all-pipelines'] as const,
    queryFn: async (): Promise<PipelineStageCandidate[]> => {
      if (!tenantId) return [];

      // Step 1: load all of the tenant's pipelines (used for name lookup
      // and ordering). Tenant-scoped at the application layer per AR21.
      const { data: pipelines, error: pipelineError } = await supabase
        .from('pipelines')
        .select('id, name, priority')
        .eq('tenant_id', tenantId)
        .order('priority', { ascending: true });

      if (pipelineError) {
        throw new Error(`Could not load pipelines: ${pipelineError.message}`);
      }

      const pipelineList = (pipelines as RawPipelineRow[] | null) ?? [];
      if (pipelineList.length === 0) return [];

      const pipelineNameById = new Map<number, string>(
        pipelineList.map((p) => [p.id, p.name]),
      );
      const pipelinePriorityById = new Map<number, number>(
        pipelineList.map((p) => [p.id, p.priority]),
      );
      const pipelineIds = pipelineList.map((p) => p.id);

      // Step 2: load all stages across those pipelines.
      const { data: stages, error: stagesError } = await supabase
        .from('stages')
        .select('id, name, stage_order, pipeline_id')
        .in('pipeline_id', pipelineIds);

      if (stagesError) {
        throw new Error(`Could not load stages: ${stagesError.message}`);
      }

      // Sort by pipeline priority first, then by stage_order within each
      // pipeline. Keeps the dropdown listing predictable.
      const stageList = (stages as RawStageRow[] | null) ?? [];
      stageList.sort((a, b) => {
        const pipelineDelta =
          (pipelinePriorityById.get(a.pipeline_id) ?? 0) -
          (pipelinePriorityById.get(b.pipeline_id) ?? 0);
        if (pipelineDelta !== 0) return pipelineDelta;
        return a.stage_order - b.stage_order;
      });

      return stageList.map((row) => ({
        id: row.id,
        stageName: row.name,
        stageOrder: row.stage_order,
        pipelineId: row.pipeline_id,
        pipelineName: pipelineNameById.get(row.pipeline_id) ?? '(unknown pipeline)',
      }));
    },
    enabled: !!tenantId,
    staleTime: FIVE_MINUTES,
  });
}
